import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, Tag, Row, Col, Statistic, message, Space, Drawer, Badge, Timeline, Avatar, Divider } from 'antd';
import { PlusOutlined, MessageOutlined, ClockCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import apiService from 'services/api/apiService';

const { TextArea } = Input;
const priorityColors: Record<string, string> = { low: 'default', medium: 'processing', high: 'warning', urgent: 'error' };
const statusColors: Record<string, string> = { open: 'red', in_progress: 'orange', pending_employee: 'purple', resolved: 'green', closed: 'default' };
const categoryIcons: Record<string, string> = { payroll: '💰', leave: '🌴', benefits: '🏥', policy: '📋', it_access: '💻', general: '❓', complaint: '⚠️', onboarding: '👋' };
const slaMap: Record<string, number> = { urgent: 4, high: 8, medium: 24, low: 72 };

const HelpDeskDashboard: React.FC = () => {
  const [tickets, setTickets] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [comments, setComments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [ticketDrawer, setTicketDrawer] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [createForm] = Form.useForm();
  const [commentForm] = Form.useForm();

  useEffect(() => { fetchAll(); }, [statusFilter]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tr, sr, er] = await Promise.all([
        apiService.getHelpdeskTickets(statusFilter || undefined),
        apiService.getHelpdeskSummary(),
        apiService.getEmployees(),
      ]);
      if (tr.success) setTickets(tr.data || []);
      if (sr.success) setSummary(sr.data);
      if (er.success) setEmployees(er.data || []);
    } finally { setLoading(false); }
  };

  const openTicket = async (ticket: any) => {
    setSelectedTicket(ticket);
    setTicketDrawer(true);
    const r = await apiService.getTicketComments(ticket.ticket_id);
    if (r.success) setComments(r.data || []);
  };

  const handleCreate = async (values: any) => {
    const r = await apiService.createHelpdeskTicket(values);
    if (r.success) { message.success(`Ticket ${r.ticket_id} created`); setCreateModal(false); createForm.resetFields(); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const handleComment = async (values: any) => {
    const r = await apiService.addTicketComment({ ...values, ticket_id: selectedTicket.ticket_id });
    if (r.success) {
      message.success('Comment added');
      commentForm.resetFields();
      const cr = await apiService.getTicketComments(selectedTicket.ticket_id);
      if (cr.success) setComments(cr.data || []);
      if (values.new_status) fetchAll();
    } else message.error(r.error || 'Failed');
  };

  const handleStatusUpdate = async (ticketId: number, status: string) => {
    const r = await apiService.updateHelpdeskTicket({ id: ticketId, status });
    if (r.success) { message.success('Status updated'); fetchAll(); }
  };

  const getSLAProgress = (ticket: any) => {
    const slaHours = slaMap[ticket.priority] || 24;
    const elapsed = dayjs().diff(dayjs(ticket.created_at), 'hour');
    const pct = Math.min((elapsed / slaHours) * 100, 100);
    return { pct, overdue: ticket.is_overdue };
  };

  const columns = [
    { title: 'Ticket', dataIndex: 'ticket_id', render: (v: string) => <code style={{ fontSize: 11 }}>{v}</code>, width: 130 },
    { title: 'Category', dataIndex: 'category', render: (v: string) => <>{categoryIcons[v] || '❓'} {v?.replace('_',' ')}</>, width: 120 },
    { title: 'Subject', dataIndex: 'subject', render: (v: string, r: any) => <><div>{v}</div><div style={{ fontSize: 12, color: '#666' }}>{r.employee_name}</div></> },
    { title: 'Priority', dataIndex: 'priority', render: (v: string) => <Badge status={priorityColors[v] as any} text={v?.toUpperCase()} />, width: 90 },
    { title: 'Status', dataIndex: 'status', render: (v: string, r: any) => <><Tag color={statusColors[v]}>{v?.replace('_',' ').toUpperCase()}</Tag>{r.is_overdue ? <Tag color="red">OVERDUE</Tag> : ''}</>, width: 160 },
    { title: 'Assigned', dataIndex: 'assigned_to_name', render: (v: string) => v || <span style={{ color: '#999' }}>Unassigned</span>, width: 120 },
    { title: 'SLA', key: 'sla', width: 80, render: (_: any, r: any) => {
      const { pct, overdue } = getSLAProgress(r);
      return <div style={{ width: 60, height: 6, background: '#f0f0f0', borderRadius: 3 }}><div style={{ width: `${pct}%`, height: '100%', background: overdue ? '#ff4d4f' : pct > 75 ? '#faad14' : '#52c41a', borderRadius: 3 }} /></div>;
    }},
    { title: 'Created', dataIndex: 'created_at', render: (v: string) => (() => { const d = dayjs(v); const diff = dayjs().diff(d, 'minute'); if (diff < 60) return diff + 'm ago'; if (diff < 1440) return Math.floor(diff/60) + 'h ago'; return Math.floor(diff/1440) + 'd ago'; })(), width: 100 },
    { title: 'Actions', render: (_: any, r: any) => (
      <Space>
        <Button size="small" icon={<MessageOutlined />} onClick={() => openTicket(r)}>View</Button>
        {r.status === 'open' && <Button size="small" onClick={() => handleStatusUpdate(r.id, 'in_progress')}>Start</Button>}
        {r.status === 'in_progress' && <Button size="small" type="primary" onClick={() => handleStatusUpdate(r.id, 'resolved')}>Resolve</Button>}
      </Space>
    ), width: 200 },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h1>HR Helpdesk</h1>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={4}><Card><Statistic title="Open" value={summary?.open_tickets || 0} valueStyle={{ color: '#ff4d4f' }} prefix={<ExclamationCircleOutlined />} /></Card></Col>
        <Col span={4}><Card><Statistic title="In Progress" value={summary?.in_progress || 0} valueStyle={{ color: '#faad14' }} /></Card></Col>
        <Col span={4}><Card><Statistic title="Resolved" value={summary?.resolved || 0} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} /></Card></Col>
        <Col span={4}><Card><Statistic title="Overdue" value={summary?.overdue || 0} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
        <Col span={4}><Card><Statistic title="Avg Resolution" value={summary?.avg_resolution_hours || 0} suffix="hrs" precision={1} prefix={<ClockCircleOutlined />} /></Card></Col>
        <Col span={4}><Card><Statistic title="Satisfaction" value={summary?.avg_satisfaction || 0} suffix="/5" precision={1} /></Card></Col>
      </Row>

      <Card
        title="Support Tickets"
        loading={loading}
        extra={
          <Space>
            <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 140 }} allowClear placeholder="Filter by status">
              {['open','in_progress','pending_employee','resolved','closed'].map(s => <Select.Option key={s} value={s}>{s.replace('_',' ')}</Select.Option>)}
            </Select>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModal(true)}>New Ticket</Button>
          </Space>
        }
      >
        <Table columns={columns} dataSource={tickets} rowKey="id" pagination={{ pageSize: 10 }} scroll={{ x: 1200 }} />
      </Card>

      {/* Create Ticket Modal */}
      <Modal title="Create Support Ticket" open={createModal} onCancel={() => { setCreateModal(false); createForm.resetFields(); }} footer={null} width={600}>
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children">{employees.map(e => <Select.Option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.department}</Select.Option>)}</Select>
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="category" label="Category" initialValue="general" rules={[{ required: true }]}>
              <Select>{Object.keys(categoryIcons).map(k => <Select.Option key={k} value={k}>{categoryIcons[k]} {k.replace('_',' ')}</Select.Option>)}</Select>
            </Form.Item></Col>
            <Col span={12}><Form.Item name="priority" label="Priority" initialValue="medium" rules={[{ required: true }]}>
              <Select><Select.Option value="low">Low (72hr SLA)</Select.Option><Select.Option value="medium">Medium (24hr SLA)</Select.Option><Select.Option value="high">High (8hr SLA)</Select.Option><Select.Option value="urgent">Urgent (4hr SLA)</Select.Option></Select>
            </Form.Item></Col>
          </Row>
          <Form.Item name="subject" label="Subject" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}><TextArea rows={4} /></Form.Item>
          <Space><Button type="primary" htmlType="submit">Create Ticket</Button><Button onClick={() => setCreateModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* Ticket Detail Drawer */}
      <Drawer title={selectedTicket ? `${selectedTicket.ticket_id} — ${selectedTicket.subject}` : ''} open={ticketDrawer} onClose={() => setTicketDrawer(false)} width={600}>
        {selectedTicket && (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}><Statistic title="Status" value={selectedTicket.status?.replace('_',' ')} /></Col>
              <Col span={8}><Statistic title="Priority" value={selectedTicket.priority} /></Col>
              <Col span={8}><Statistic title="SLA" value={`${slaMap[selectedTicket.priority]}hrs`} /></Col>
            </Row>
            <div style={{ marginBottom: 16 }}>
              <strong>Description:</strong>
              <p style={{ color: '#666', marginTop: 4 }}>{selectedTicket.description}</p>
            </div>
            <Divider>Comments ({comments.length})</Divider>
            <Timeline items={comments.map(c => ({
              color: c.author_type === 'hr' ? 'blue' : c.author_type === 'system' ? 'gray' : 'green',
              children: (
                <div>
                  <strong>{c.author_name || c.author_type}</strong> <span style={{ fontSize: 11, color: '#999' }}>{(() => { const d = dayjs(c.created_at); const diff = dayjs().diff(d, 'minute'); if (diff < 60) return diff + 'm ago'; if (diff < 1440) return Math.floor(diff/60) + 'h ago'; return Math.floor(diff/1440) + 'd ago'; })()}</span>
                  {c.is_internal && <Tag color="orange" style={{ marginLeft: 8, fontSize: 10 }}>Internal</Tag>}
                  <p style={{ margin: '4px 0', color: '#444' }}>{c.comment}</p>
                </div>
              )
            }))} />
            <Divider>Add Response</Divider>
            <Form form={commentForm} layout="vertical" onFinish={handleComment}>
              <Form.Item name="comment" rules={[{ required: true }]}><TextArea rows={3} placeholder="Type your response..." /></Form.Item>
              <Row gutter={16}>
                <Col span={12}><Form.Item name="author_id" label="From" rules={[{ required: true }]}><Select showSearch optionFilterProp="children">{employees.map(e => <Select.Option key={e.id} value={e.id}>{e.first_name} {e.last_name}</Select.Option>)}</Select></Form.Item></Col>
                <Col span={12}><Form.Item name="new_status" label="Update Status"><Select allowClear>{['in_progress','pending_employee','resolved','closed'].map(s => <Select.Option key={s} value={s}>{s.replace('_',' ')}</Select.Option>)}</Select></Form.Item></Col>
              </Row>
              <Form.Item name="author_type" initialValue="hr" hidden><Input /></Form.Item>
              <Button type="primary" htmlType="submit" icon={<MessageOutlined />}>Send Response</Button>
            </Form>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default HelpDeskDashboard;
